// Shared zoom-state hook for Timeline (TTS) and STSMasterTimeline (STS).
//
// Both timelines drive an identical "sticky-fit" zoom model: a pxPerSec axis
// with a hard ceiling at PX_PER_SEC_MAX, a dynamic floor at fitPxPerSec
// ("show-all" — exact wrapW/totalDur), and a sticky-fit flag that re-derives
// pxPerSec from fitPxPerSec each render so totalDur changes don't introduce
// a frame of horizontal-scrollbar before a useEffect reacts.
//
// Internal storage: a single `pxPerSecManual` integer where the sentinel
// `-1` means sticky-fit is active. Both flags always moved in lockstep in
// the pre-refactor code (sticky=true ↔ manual=-1), so collapsing them
// removes a class of "out-of-sync" bugs without changing behavior.
//
// Re-fit-on-shrink: when the asset's effective boundary shrinks (e.g. user
// lowers a target/custom duration, or switches to a shorter video), we snap
// back to sticky-fit so the now-tiny timeline doesn't survive in viewport
// at the old manual zoom (it'd fill ~1/3 of the wrap). Only fires on shrink
// — extending past the old boundary leaves the user's chosen zoom alone.

import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from 'react';
import { flushSync } from 'react-dom';

// Same values both pre-refactor timelines used.
const PX_PER_SEC_MAX = 500;
const PX_PER_SEC_FLOOR = 0.5;
const FIT_FILL_RATIO = 1;
// Stepper multiplier — both pre-refactor timelines used 1.4x.
const ZOOM_STEP = 1.4;

interface Args {
  wrapW: number;
  totalDur: number;
  scrollWrapRef: RefObject<HTMLDivElement | null>;
  /** Re-fit-on-shrink trigger. When this value shrinks ≥ 0.01s vs the prior
   *  render, zoom snaps back to sticky-fit. Pass the asset's "official"
   *  boundary (target/custom/video duration) — NOT totalDur, which would
   *  also re-trigger when a user drags a chip back from overtime. */
  shrinkOnBoundary?: number;
}

interface ZoomFitToArgs {
  /** Span in seconds to fit into the visible area. */
  span: number;
  /** Left edge of the span on the timeline, in seconds. */
  leftSec: number;
  /** Fraction of wrapW the span should occupy after the fit. Default 0.92. */
  ratio?: number;
  /** Additional left-margin as a fraction of wrapW. Default 0.04. */
  marginRatio?: number;
}

interface ZoomFitToOpts {
  /** When set, scrollLeft is anchored so the timeline-time currently under
   *  this client-X stays under the same client-X after the zoom commits.
   *  Used for chip-anchored auto-zoom so the pixel under the cursor doesn't
   *  jump. Mutually exclusive with the default left-margin behavior. */
  anchorClientX?: number;
}

export interface UseTimelineZoomReturn {
  pxPerSec: number;
  fitPxPerSec: number;
  stickyFit: boolean;
  pxPerSecManual: number; // -1 sentinel = sticky-fit active
  setPxPerSecManual: (n: number) => void;
  zoomIn: () => void;
  zoomOut: () => void;
  /** Sticky-fit (back to wrap-width fit). */
  zoomFit: () => void;
  /** Fit a specific span (e.g. block/clip bounding box). Falls back to
   *  plain `zoomFit` when the requested zoom would be at-or-below show-all.
   *  Optional `opts.anchorClientX` anchors the post-zoom scrollLeft so the
   *  pixel under that client-X stays over the same timeline-time. */
  zoomFitTo: (args: ZoomFitToArgs, opts?: ZoomFitToOpts) => void;
}

export function useTimelineZoom({
  wrapW,
  totalDur,
  scrollWrapRef,
  shrinkOnBoundary,
}: Args): UseTimelineZoomReturn {
  // -1 sentinel == sticky-fit. Default sticky so first render is show-all.
  const [pxPerSecManual, setPxPerSecManualState] = useState<number>(-1);

  // Fit-to-view px/sec uses totalDur (not boundary) so an overtime timeline
  // still fits on screen at "show all".
  const fitPxPerSec = useMemo(() => {
    if (!wrapW || !totalDur) return PX_PER_SEC_FLOOR;
    const raw = (wrapW * FIT_FILL_RATIO) / totalDur;
    // PX_PER_SEC_MAX caps MANUAL zoom-in so the user can't accidentally
    // scroll into 100,000 px/sec land. The fit-scale must NOT be capped —
    // otherwise short timelines (≤2s) only fill a fraction of the wrap
    // width, leaving an empty band on the right (FB#13 follow-up).
    return Math.max(PX_PER_SEC_FLOOR, raw);
  }, [wrapW, totalDur]);

  const stickyFit = pxPerSecManual === -1;
  const pxPerSec = stickyFit ? fitPxPerSec : pxPerSecManual;

  // Re-fit-on-shrink: snap to sticky when the boundary shrinks ≥ 0.01s vs
  // prev. Watching boundary (not totalDur) is intentional — totalDur also
  // grows when the user drags a chip into overtime, and we don't want a
  // chip drag-back to re-snap zoom out from under the user.
  const prevBoundaryRef = useRef<number>(shrinkOnBoundary ?? 0);
  useEffect(() => {
    if (shrinkOnBoundary === undefined) return;
    const prev = prevBoundaryRef.current;
    prevBoundaryRef.current = shrinkOnBoundary;
    if (
      shrinkOnBoundary > 0 &&
      prev > 0 &&
      shrinkOnBoundary < prev - 0.01
    ) {
      setPxPerSecManualState(-1);
    }
  }, [shrinkOnBoundary]);

  const setPxPerSecManual = useCallback((n: number) => {
    setPxPerSecManualState(n);
  }, []);

  const zoomIn = useCallback(() => {
    const base = pxPerSecManual === -1 ? fitPxPerSec : pxPerSecManual;
    const next = Math.min(PX_PER_SEC_MAX, base * ZOOM_STEP);
    setPxPerSecManualState(next);
  }, [pxPerSecManual, fitPxPerSec]);

  const zoomOut = useCallback(() => {
    const base = pxPerSecManual === -1 ? fitPxPerSec : pxPerSecManual;
    const next = base / ZOOM_STEP;
    if (next <= fitPxPerSec + 0.01) {
      setPxPerSecManualState(-1);
    } else {
      setPxPerSecManualState(next);
    }
  }, [pxPerSecManual, fitPxPerSec]);

  const zoomFit = useCallback(() => {
    setPxPerSecManualState(-1);
  }, []);

  // pxPerSec ref so zoomFitTo can read the current value without depending
  // on it (and re-creating each render, which would ripple through callers).
  const pxPerSecRef = useRef<number>(pxPerSec);
  pxPerSecRef.current = pxPerSec;

  const zoomFitTo = useCallback(
    (
      { span, leftSec, ratio = 0.92, marginRatio = 0.04 }: ZoomFitToArgs,
      opts?: ZoomFitToOpts,
    ) => {
      if (!wrapW || span <= 0) {
        setPxPerSecManualState(-1);
        return;
      }
      const raw = (wrapW * ratio) / span;
      if (raw <= fitPxPerSec + 0.01) {
        // Requested span is wider than show-all → just sticky-fit.
        setPxPerSecManualState(-1);
        return;
      }
      const targetPx = Math.min(PX_PER_SEC_MAX, raw);
      const wrap = scrollWrapRef.current;

      // Anchored zoom — capture the timeline-time currently under
      // anchorClientX BEFORE the zoom commits so we can re-pin the same time
      // to the same viewport-X afterwards. This is what makes a chip click
      // "zoom into the chip without yanking the cursor off it".
      //
      // Visibility clamp: on long timelines the source span (e.g. a 3s clip
      // on a 5min video) is only a few pixels wide at sticky-fit, so a
      // millimetre of cursor offset translates to many seconds of timeline
      // offset — the raw anchor formula can land scrollLeft so far off the
      // span that the chip itself is no longer in view. Clamp the result so
      // the span stays inside the new viewport with the same `marginRatio`
      // breathing room the no-anchor branch uses.
      let anchoredScrollLeft: number | null = null;
      if (
        opts?.anchorClientX !== undefined &&
        wrap &&
        pxPerSecRef.current > 0
      ) {
        const wrapRect = wrap.getBoundingClientRect();
        const viewportX = opts.anchorClientX - wrapRect.left;
        // Clamp the anchor to the span's actual time range. Chip elements
        // are min-width-pinned at low zoom (e.g. a 3s clip on a 5min
        // timeline at sticky-fit → 12 logical px but 24px visual width)
        // so the user can click within the chip element at a viewportX
        // that maps to a timeline position past the chip's real right
        // edge. Without this clamp the post-zoom cursor lands at that
        // phantom position rather than on the chip.
        const rawAnchor =
          (viewportX + wrap.scrollLeft) / pxPerSecRef.current;
        const anchorTimelineSec = Math.max(
          leftSec,
          Math.min(leftSec + span, rawAnchor),
        );
        const candidate = anchorTimelineSec * targetPx - viewportX;
        const spanLeftPx = leftSec * targetPx;
        const spanRightPx = (leftSec + span) * targetPx;
        const marginPx = wrapW * marginRatio;
        // Min: span's right edge sits at (wrapW - marginPx). Max: span's
        // left edge sits at marginPx. When the span is wider than the
        // wrap (impossible at ratio ≤ 1 but defensive) min > max → the
        // span's right edge wins (Math.max picks it last).
        const minScroll = Math.max(0, spanRightPx + marginPx - wrapW);
        const maxScroll = Math.max(minScroll, spanLeftPx - marginPx);
        anchoredScrollLeft = Math.max(
          minScroll,
          Math.min(maxScroll, candidate),
        );
      }

      // flushSync so the new trackWidth lands in the DOM BEFORE we set
      // scrollLeft — otherwise React batches the state change, the browser
      // repaints with the old trackWidth, scrollLeft pins to t=0, and only
      // then the new layout commits, producing a visible "jump to t=0
      // first" flash.
      flushSync(() => {
        setPxPerSecManualState(targetPx);
      });
      if (wrap) {
        const maxScroll = Math.max(0, wrap.scrollWidth - wrap.clientWidth);
        const targetScrollLeft =
          anchoredScrollLeft !== null
            ? Math.max(0, Math.min(maxScroll, anchoredScrollLeft))
            : Math.max(0, leftSec * targetPx - wrapW * marginRatio);
        wrap.scrollLeft = targetScrollLeft;
      }
    },
    [wrapW, fitPxPerSec, scrollWrapRef],
  );

  return {
    pxPerSec,
    fitPxPerSec,
    stickyFit,
    pxPerSecManual,
    setPxPerSecManual,
    zoomIn,
    zoomOut,
    zoomFit,
    zoomFitTo,
  };
}
